import { useQuery } from '@apollo/client';
import {
  Entities,
  Instruments,
  Research,
  PortfolioEntity,
  PortfolioItems,
  PortfolioResponse,
  Portfolios,
  Events,
  FiltersData,
  Funds,
  RequestTab,
} from 'modules/common/PortfolioManager/types';
import { SortParam } from 'shared/types/dataTable';
import { ArtifactId, PortfolioItem } from 'shared/types/portfolios';
import { EventFilters } from 'shared/types/eventFilter';
import { makeMutation } from '../utils';
import { RatingNews, Uploads } from '../../PortfolioManager/types';
import {
  ADD_PORTFOLIO_ITEM,
  COPY_PORTFOLIO_ITEMS,
  COPY_ALL_PORTFOLIO_ITEMS,
  CREATE_PORTFOLIO,
  UPDATE_PORTFOLIO,
  DELETE_PORTFOLIOS,
  DELETE_ENTITIES,
  MOVE_PORTFOLIO_ITEMS,
  MOVE_ALL_PORTFOLIO_ITEMS,
  MOVE_RESEARCHES,
  COPY_RESEARCHES,
  COPY_ALL_RESEARCHES,
  ADD_RESEARCH_ITEMS,
  DELETE_RESEARCH_ITEMS,
  ADD_INSTRUMENTS,
  DELETE_INSTRUMENTS,
  COPY_INSTRUMENTS,
  COPY_ALL_INSTRUMENTS,
  MOVE_INSTRUMENTS,
  ADD_PORTFOLIO_RATING_NEWS,
  COPY_PORTFOLIO_RATING_NEWS,
  COPY_ALL_PORTFOLIO_RATING_NEWS,
  MOVE_PORTFOLIO_RATING_NEWS,
  MOVE_ALL_PORTFOLIO_RATING_NEWS,
  DELETE_PORTFOLIO_RATING_NEWS,
  ADD_EVENTS,
  COPY_EVENTS,
  COPY_ALL_EVENTS,
  MOVE_EVENTS,
  DELETE_EVENTS,
  MOVE_ALL_INSTRUMENTS,
  MOVE_ALL_RESEARCHES,
  MOVE_ALL_EVENTS,
  MOVE_FUNDS,
  ADD_FUNDS,
  COPY_FUNDS,
  COPY_ALL_FUNDS,
  MOVE_ALL_FUNDS,
  DELETE_FUNDS,
  TOGGLE_SIG_MONITORING,
  DELETE_UPLOAD,
} from './mutations';
import {
  GET_ENTITIES_QUERY,
  GET_ENTITIES_QUERY_BY_ARTIFACT_ID,
  GET_ENTITY_QUERY,
  GET_EVENTS_QUERY,
  GET_FUNDS_QUERY,
  GET_INSTRUMENTS_QUERY,
  GET_PORTFOLIOS_QUERY,
  GET_PORTFOLIO_QUERY,
  GET_RATING_NEWS_QUERY,
  GET_RESEARCH_QUERY,
  GET_UPLOADS_QUERY,
} from './queries';

/* ******************************************************************** */
/*                              FETCH FUNCTIONS                         */
/* ******************************************************************** */

export const useFetchPortfolios = (
  start = 0,
  limit = 20,
  sort?: SortParam[],
  filter?: FiltersData,
) =>
  useQuery<Portfolios>(GET_PORTFOLIOS_QUERY, {
    variables: { start, limit, sort, filter },
  });

export const useFetchPortfolio = (id: string) =>
  useQuery<PortfolioResponse>(GET_PORTFOLIO_QUERY, { variables: { id } });

export const useFetchPortfolioItems = (
  portfolioId: string,
  start = 0,
  limit = 20,
  sort?: SortParam[],
  requestedTab?: RequestTab,
  filter?: FiltersData,
) =>
  useQuery<PortfolioItems<Entities>>(GET_ENTITIES_QUERY, {
    variables: { portfolioId, start, limit, sort, requestedTab, filter },
  });

export const useFetchPortfolioItemsByArtifactId = (
  artifactId: string,
  start = 0,
  limit = 1,
  sort?: SortParam[],
) =>
  useQuery<PortfolioItems<Entities>>(GET_ENTITIES_QUERY_BY_ARTIFACT_ID, {
    variables: { artifactIds: [artifactId], start, limit, sort },
  });

export const useFetchPortfolioItem = (id: string) =>
  useQuery<PortfolioEntity>(GET_ENTITY_QUERY, { variables: { id } });

export const useFetchPortfolioInstruments = (
  portfolioId: string,
  start = 0,
  limit = 20,
  sort?: SortParam[],
  filter?: FiltersData,
) =>
  useQuery<PortfolioItems<Instruments>>(GET_INSTRUMENTS_QUERY, {
    variables: { portfolioId, start, limit, sort, filter },
  });

export const useFetchPortfolioFunds = (
  portfolioId: string,
  start = 0,
  limit = 20,
  sort?: SortParam[],
  filter?: FiltersData,
) =>
  useQuery<PortfolioItems<Funds>>(GET_FUNDS_QUERY, {
    variables: { portfolioId, start, limit, sort, filter },
  });

export const useFetchPortfolioRatingNews = (
  portfolioId: string,
  start = 0,
  limit = 20,
  sort?: SortParam[],
) =>
  useQuery<PortfolioItems<RatingNews>>(GET_RATING_NEWS_QUERY, {
    variables: { portfolioId, start, limit, sort },
  });

export const useFetchPortfolioResearch = (
  portfolioId: string,
  language = 'en',
  start = 0,
  limit = 20,
  sort?: SortParam[],
) =>
  useQuery<PortfolioItems<Research>>(GET_RESEARCH_QUERY, {
    variables: { portfolioId, language, start, limit, sort },
  });

export const useFetchPortfolioEvents = (
  portfolioId: string,
  start = 0,
  limit = 20,
  sort?: SortParam[],
  filter?: EventFilters,
) =>
  useQuery<PortfolioItems<Events>>(GET_EVENTS_QUERY, {
    variables: { portfolioId, start, limit, sort, filter },
  });

export const useFetchPortfolioUploads = (
  portfolioId: string,
  start = 0,
  limit = 20,
  sort?: SortParam[],
) =>
  useQuery<PortfolioItems<Uploads>>(GET_UPLOADS_QUERY, {
    variables: { portfolioId, start, limit, sort },
  });

/* ******************************************************************** */
/*                           ADD ITEMS FUNCTIONS                        */
/* ******************************************************************** */

const useAddEntities = makeMutation<{
  portfolioId: string;
  artifactIdList: ArtifactId[];
}>(ADD_PORTFOLIO_ITEM);

const useAddInstruments = makeMutation<{
  portfolioId: string;
  artifactIdList: ArtifactId[];
}>(ADD_INSTRUMENTS);

const useAddFunds = makeMutation<{
  portfolioId: string;
  artifactIdList: ArtifactId[];
}>(ADD_FUNDS);

export const useAddRatingNews = makeMutation<{
  portfolioId: string;
  artifactIdList: ArtifactId[];
}>(ADD_PORTFOLIO_RATING_NEWS);

export const useAddToResearch = makeMutation<{
  artifactIdList: ArtifactId[];
  portfolioId: string;
}>(ADD_RESEARCH_ITEMS);

export const useAddEvents = makeMutation<{
  portfolioId: string;
  artifactIdList: ArtifactId[];
}>(ADD_EVENTS);

export const useAddToPortfolio = (itemType: PortfolioItem) => {
  const addEntities = useAddEntities();
  const addInstruments = useAddInstruments();
  const addFunds = useAddFunds();
  const addToResearch = useAddToResearch();
  const addRatingNews = useAddRatingNews();
  const addEvents = useAddEvents();

  if (itemType === 'INSTRUMENT' || itemType === 'ISSUE') {
    return addInstruments;
  }

  if (itemType === 'FUND') {
    return addFunds;
  }

  if (itemType === 'RATING_NEWS' || itemType === 'MONITORING_NOTES') {
    return addRatingNews;
  }

  if (itemType === 'RESEARCH' || itemType === 'COMMENTARY' || itemType === 'COMPANY_NEWS') {
    return addToResearch;
  }

  if (itemType === 'EVENT') {
    return addEvents;
  }
  return addEntities;
};

/* ******************************************************************** */
/*                          COPY ITEMS FUNCTIONS                        */
/* ******************************************************************** */

export const useCopyEntities = makeMutation<{
  portfolioId: string;
  portfolioItemsIds: string[];
  sourcePortfolioId: string;
}>(COPY_PORTFOLIO_ITEMS);

export const useCopyInstruments = makeMutation<{
  instrumentsIds: string[];
  portfolioId: string;
  sourcePortfolioId: string;
}>(COPY_INSTRUMENTS);

export const useCopyFunds = makeMutation<{
  isins: string[];
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(COPY_FUNDS);

export const useCopyRatingNews = makeMutation<{
  artifactIds: string[];
  portfolioId: string;
  sourcePortfolioId: string;
}>(COPY_PORTFOLIO_RATING_NEWS);

export const useCopyResearchItems = makeMutation<{
  portfolioId: string;
  artifactIds: string[];
  sourcePortfolioId: string;
}>(COPY_RESEARCHES);

export const useCopyEvents = makeMutation<{
  portfolioId: string;
  eventsIds: string[];
  sourcePortfolioId: string;
}>(COPY_EVENTS);

type CopyVariables = {
  portfolioId: string;
  itemIds: string[];
  sourcePortfolioId: string;
};

export const useCopyPortfolioItems = (itemType: PortfolioItem) => {
  const copyEntities = useCopyEntities();
  const copyInstruments = useCopyInstruments();
  const copyFunds = useCopyFunds();
  const copyRatingNews = useCopyRatingNews();
  const copyResearches = useCopyResearchItems();
  const copyEvents = useCopyEvents();

  if (itemType === 'INSTRUMENT' || itemType === 'ISSUE') {
    return ({ portfolioId, itemIds, sourcePortfolioId }: CopyVariables) =>
      copyInstruments({ portfolioId, instrumentsIds: itemIds, sourcePortfolioId });
  }

  if (itemType === 'FUND') {
    return ({ portfolioId, itemIds, sourcePortfolioId }: CopyVariables) =>
      copyFunds({ destinationPortfolioId: portfolioId, isins: itemIds, sourcePortfolioId });
  }

  if (itemType === 'RATING_NEWS' || itemType === 'MONITORING_NOTES') {
    return ({ portfolioId, itemIds, sourcePortfolioId }: CopyVariables) =>
      copyRatingNews({ portfolioId, artifactIds: itemIds, sourcePortfolioId });
  }

  if (itemType === 'RESEARCH' || itemType === 'COMMENTARY' || itemType === 'COMPANY_NEWS') {
    return ({ portfolioId, itemIds, sourcePortfolioId }: CopyVariables) =>
      copyResearches({ portfolioId, artifactIds: itemIds, sourcePortfolioId });
  }

  if (itemType === 'EVENT') {
    return ({ portfolioId, itemIds, sourcePortfolioId }: CopyVariables) =>
      copyEvents({ portfolioId, eventsIds: itemIds, sourcePortfolioId });
  }

  return ({ portfolioId, itemIds, sourcePortfolioId }: CopyVariables) =>
    copyEntities({ portfolioId, portfolioItemsIds: itemIds, sourcePortfolioId });
};

/* ******************************************************************** */
/*                          COPY ALL ITEMS FUNCTIONS                     */
/* ******************************************************************** */

export const useCopyAllEntities = makeMutation<{
  portfolioId: string;
  sourcePortfolioId: string;
}>(COPY_ALL_PORTFOLIO_ITEMS);

export const useCopyAllInstruments = makeMutation<{
  portfolioId: string;
  sourcePortfolioId: string;
}>(COPY_ALL_INSTRUMENTS);

export const useCopyAllFunds = makeMutation<{
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(COPY_ALL_FUNDS);

export const useCopyAllRatingNews = makeMutation<{
  portfolioId: string;
  sourcePortfolioId: string;
}>(COPY_ALL_PORTFOLIO_RATING_NEWS);

export const useCopyAllResearchItems = makeMutation<{
  portfolioId: string;
  sourcePortfolioId: string;
}>(COPY_ALL_RESEARCHES);

export const useCopyAllEvents = makeMutation<{
  portfolioId: string;
  sourcePortfolioId: string;
}>(COPY_ALL_EVENTS);

type CopyAllVariables = {
  portfolioId: string;
  sourcePortfolioId: string;
};

export const useCopyAllPortfolioItems = (itemType: PortfolioItem) => {
  const copyAllEntities = useCopyAllEntities();
  const copyAllInstruments = useCopyAllInstruments();
  const copyAllFunds = useCopyAllFunds();
  const copyAllRatingNews = useCopyAllRatingNews();
  const copyAllResearches = useCopyAllResearchItems();
  const copyAllEvents = useCopyAllEvents();

  if (itemType === 'INSTRUMENT' || itemType === 'ISSUE') {
    return ({ portfolioId, sourcePortfolioId }: CopyAllVariables) =>
      copyAllInstruments({ portfolioId, sourcePortfolioId });
  }

  if (itemType === 'FUND') {
    return ({ portfolioId, sourcePortfolioId }: CopyAllVariables) =>
      copyAllFunds({ destinationPortfolioId: portfolioId, sourcePortfolioId });
  }

  if (itemType === 'RATING_NEWS' || itemType === 'MONITORING_NOTES') {
    return ({ portfolioId, sourcePortfolioId }: CopyAllVariables) =>
      copyAllRatingNews({ portfolioId, sourcePortfolioId });
  }

  if (itemType === 'RESEARCH' || itemType === 'COMMENTARY' || itemType === 'COMPANY_NEWS') {
    return ({ portfolioId, sourcePortfolioId }: CopyAllVariables) =>
      copyAllResearches({ portfolioId, sourcePortfolioId });
  }

  if (itemType === 'EVENT') {
    return ({ portfolioId, sourcePortfolioId }: CopyAllVariables) =>
      copyAllEvents({ portfolioId, sourcePortfolioId });
  }

  return ({ portfolioId, sourcePortfolioId }: CopyAllVariables) =>
    copyAllEntities({ portfolioId, sourcePortfolioId });
};

/* ******************************************************************** */
/*                          MOVE ITEMS FUNCTIONS                        */
/* ******************************************************************** */

export const useMoveEntities = makeMutation<{
  portfolioItemsIds: string[];
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_PORTFOLIO_ITEMS);

export const useMoveInstruments = makeMutation<{
  instrumentsIds: string[];
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_INSTRUMENTS);

export const useMoveFunds = makeMutation<{
  isins: string[];
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_FUNDS);

export const useMoveRatingNews = makeMutation<{
  destinationPortfolioId: string;
  artifactIds: string[];
  sourcePortfolioId: string;
}>(MOVE_PORTFOLIO_RATING_NEWS);

export const useMoveResearchItems = makeMutation<{
  artifactIds: string[];
  destinationPortfolioId: string;
  sourcePortfolioId?: string;
}>(MOVE_RESEARCHES);

export const useMoveEvents = makeMutation<{
  destinationPortfolioId: string;
  eventsIds: string[];
  sourcePortfolioId: string;
}>(MOVE_EVENTS);

type MoveVariables = {
  destinationPortfolioId: string;
  itemIds: string[];
  sourcePortfolioId: string;
};

export const useMovePortfolioItems = (itemType: PortfolioItem) => {
  const moveEntities = useMoveEntities();
  const moveInstruments = useMoveInstruments();
  const moveFunds = useMoveFunds();
  const moveRatingNews = useMoveRatingNews();
  const moveResearches = useMoveResearchItems();
  const moveEvents = useMoveEvents();

  if (itemType === 'INSTRUMENT' || itemType === 'ISSUE') {
    return ({ destinationPortfolioId, itemIds, sourcePortfolioId }: MoveVariables) =>
      moveInstruments({ destinationPortfolioId, instrumentsIds: itemIds, sourcePortfolioId });
  }

  if (itemType === 'FUND') {
    return ({ destinationPortfolioId, itemIds, sourcePortfolioId }: MoveVariables) =>
      moveFunds({ destinationPortfolioId, isins: itemIds, sourcePortfolioId });
  }

  if (itemType === 'RATING_NEWS' || itemType === 'MONITORING_NOTES') {
    return ({ destinationPortfolioId, itemIds, sourcePortfolioId }: MoveVariables) =>
      moveRatingNews({ destinationPortfolioId, artifactIds: itemIds, sourcePortfolioId });
  }

  if (itemType === 'RESEARCH' || itemType === 'COMMENTARY' || itemType === 'COMPANY_NEWS') {
    return ({ destinationPortfolioId, itemIds, sourcePortfolioId }: MoveVariables) =>
      moveResearches({ artifactIds: itemIds, destinationPortfolioId, sourcePortfolioId });
  }

  if (itemType === 'EVENT') {
    return ({ destinationPortfolioId, itemIds, sourcePortfolioId }: MoveVariables) =>
      moveEvents({ destinationPortfolioId, eventsIds: itemIds, sourcePortfolioId });
  }

  return ({ destinationPortfolioId, itemIds, sourcePortfolioId }: MoveVariables) =>
    moveEntities({ destinationPortfolioId, portfolioItemsIds: itemIds, sourcePortfolioId });
};

/* ******************************************************************** */
/*                        MOVE ALL ITEMS FUNCTIONS                      */
/* ******************************************************************** */

export const useMoveAllEntities = makeMutation<{
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_ALL_PORTFOLIO_ITEMS);

export const useMoveAllInstruments = makeMutation<{
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_ALL_INSTRUMENTS);

export const useMoveAllFunds = makeMutation<{
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_ALL_FUNDS);

export const useMoveAllRatingNews = makeMutation<{
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_ALL_PORTFOLIO_RATING_NEWS);

export const useMoveAllResearchItems = makeMutation<{
  destinationPortfolioId: string;
  sourcePortfolioId: string;
}>(MOVE_ALL_RESEARCHES);

export const useMoveAllEvents = makeMutation<{
  destinationPortfolioId: string;
  sourcePortfolioId?: string;
}>(MOVE_ALL_EVENTS);

type MoveAllVariables = {
  destinationPortfolioId: string;
  sourcePortfolioId: string;
};

export const useMoveAllPortfolioItems = (itemType?: PortfolioItem) => {
  const moveAllEntities = useMoveAllEntities();
  const moveAllInstruments = useMoveAllInstruments();
  const moveAllFunds = useMoveAllFunds();
  const moveAllRatingNews = useMoveAllRatingNews();
  const moveAllResearches = useMoveAllResearchItems();
  const moveAllEvents = useMoveAllEvents();

  if (itemType === 'INSTRUMENT' || itemType === 'ISSUE') {
    return ({ destinationPortfolioId, sourcePortfolioId }: MoveAllVariables) =>
      moveAllInstruments({ destinationPortfolioId, sourcePortfolioId });
  }

  if (itemType === 'FUND') {
    return ({ destinationPortfolioId, sourcePortfolioId }: MoveAllVariables) =>
      moveAllFunds({ destinationPortfolioId, sourcePortfolioId });
  }

  if (itemType === 'RATING_NEWS' || itemType === 'MONITORING_NOTES') {
    return ({ destinationPortfolioId, sourcePortfolioId }: MoveAllVariables) =>
      moveAllRatingNews({ destinationPortfolioId, sourcePortfolioId });
  }

  if (itemType === 'RESEARCH' || itemType === 'COMMENTARY' || itemType === 'COMPANY_NEWS') {
    return ({ destinationPortfolioId, sourcePortfolioId }: MoveAllVariables) =>
      moveAllResearches({ destinationPortfolioId, sourcePortfolioId });
  }

  if (itemType === 'EVENT') {
    return ({ destinationPortfolioId, sourcePortfolioId }: MoveAllVariables) =>
      moveAllEvents({ destinationPortfolioId, sourcePortfolioId });
  }

  return ({ destinationPortfolioId, sourcePortfolioId }: MoveAllVariables) =>
    moveAllEntities({ destinationPortfolioId, sourcePortfolioId });
};
/* ******************************************************************** */
/*                         DELETE ITEMS FUNCTIONS                       */
/* ******************************************************************** */

export const useDeleteEntities = makeMutation<{
  portfolioItemsIds: string[];
  portfolioId: string;
  selectedAll: boolean;
}>(DELETE_ENTITIES);

export const useDeleteInstruments = makeMutation<{
  instrumentsIds: string[];
  portfolioId: string;
  selectedAll: boolean;
}>(DELETE_INSTRUMENTS);

export const useDeleteFunds = makeMutation<{
  isins: string[];
  portfolioId: string;
  selectedAll: boolean;
}>(DELETE_FUNDS);

export const useDeleteRatingNews = makeMutation<{
  artifactIdList: ArtifactId[];
  portfolioId: string;
  selectedAll: boolean;
}>(DELETE_PORTFOLIO_RATING_NEWS);

export const useDeleteResearchItems = makeMutation<{
  artifactIdList: ArtifactId[];
  portfolioId: string;
  selectedAll: boolean;
}>(DELETE_RESEARCH_ITEMS);

export const useDeleteEvents = makeMutation<{
  eventsIds: string[];
  portfolioId: string;
  selectedAll: boolean;
}>(DELETE_EVENTS);

export const useDeletePortfolios = makeMutation<{
  portfolioIds: string[];
}>(DELETE_PORTFOLIOS);

export const useDeleteUpload = makeMutation<{
  uploadsIds: string[];
  portfolioId: string;
}>(DELETE_UPLOAD);

/* ******************************************************************** */
/*                            OTHER FUNCTIONS                           */
/* ******************************************************************** */

export const useCreatePortfolio = makeMutation<{
  name: string;
  description?: string;
}>(CREATE_PORTFOLIO);

export const useUpdatePortfolio = makeMutation<{
  portfolioId: string;
  portfolioName?: string;
  portfolioDescription?: string;
  watchlistEnabled?: boolean;
}>(UPDATE_PORTFOLIO);

export const useToggleSigMonitoring = makeMutation<{
  portfolioId: string;
  artifactId: string;
}>(TOGGLE_SIG_MONITORING);
